import { inject, Injectable } from '@angular/core';
import { Auth, getAuth } from '@angular/fire/auth';
import { catchError, defer, from, Observable, of, switchMap, throwError } from 'rxjs';
import { RegionConfig } from 'src/environments/evironment.interface';
import { environment } from '../../../environments/environment';
import { AuthStateService } from '../auth-state/auth-state.service';
import { LoggerService } from '../logger/logger.service';
import { RegionConfigService } from '../region-config/region-config.service';
import Client, { ClientOptions, Local } from './client';
import { ClientErrorMessage } from './clienterror.interface';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private auth = inject(Auth, { optional: true });
  private logger = inject(LoggerService);
  private regionConfig = inject(RegionConfigService);
  private authStateService = inject(AuthStateService);

  async generateClient() {
    return await this.getRequestClient();
  }

  get client() {
    return defer(() => from(this.generateClient()));
  }

  request<T>(endpoint: Promise<T>): Observable<T> {
    return from(endpoint).pipe(catchError((err: unknown) => this.propagateRequestError(err)));
  }

  requestWithClient<T>(endpoint: (client: Client) => Promise<T>): Observable<T> {
    return defer(() => from(this.generateClient())).pipe(
      switchMap((client) =>
        from(endpoint(client)).pipe(
          catchError((err: unknown) => {
            // This is a hack to allow for empty responses to be returned such as unexpected end of JSON input
            if (err instanceof SyntaxError || this.getErrorCode(err)?.toString() === '12') {
              return of(null as any); // return an Observable of null
            }
            return this.propagateRequestError(err);
          })
        )
      )
    );
  }

  private getFirebaseAuth() {
    if (this.auth) {
      return this.auth;
    }
    // Avoid throwing when Firebase is not initialized (common in unit tests).
    try {
      return getAuth();
    } catch {
      return null;
    }
  }

  setRegion(region: string): void {
    this.regionConfig.setRegion(region);
  }

  getRegion(): string {
    return this.regionConfig.getRegion();
  }

  removeRegion(): void {
    this.regionConfig.removeRegion();
  }

  getConfigForRegion(): RegionConfig {
    return this.regionConfig.getConfigForRegion();
  }

  getRequestClient = async () => {
    const currentUser = this.getFirebaseAuth()?.currentUser;
    let token = '';
    if (currentUser) {
      token = (await this.getToken()) ?? '';
    }
    const regionConfig = this.regionConfig.getConfigForRegion();
    const language = localStorage.getItem('lang') || 'en';
    const env = regionConfig.api != '' ? regionConfig.api : Local;
    const clientOptions: ClientOptions = {
      requestInit: {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept-Language': language,
          'x-platform': 'web',
          'x-platform-version': navigator.userAgent,
          'x-client-version': environment.VERSION,
        },
      },
    };
    return new Client(env, clientOptions);
  };

  private async getToken(): Promise<string | null> {
    try {
      return await this.authStateService.refreshToken();
    } catch {
      return this.authStateService.forceRefresh().catch(() => null);
    }
  }

  private propagateRequestError(err: unknown): Observable<never> {
    return from(this.refreshAfterAuthorizationFailure(err)).pipe(
      switchMap(() => {
        this.logger.logClientError(err as ClientErrorMessage);
        return throwError(() => err);
      })
    );
  }

  private async refreshAfterAuthorizationFailure(err: unknown): Promise<void> {
    if (!this.isAuthorizationFailure(err) || !this.getFirebaseAuth()?.currentUser) {
      return;
    }

    // The original API failure is authoritative for the caller. A forced
    // refresh only determines whether Firebase has deauthenticated this user.
    await this.authStateService.forceRefresh().catch(() => {});
  }

  private isAuthorizationFailure(err: unknown): boolean {
    if (typeof err !== 'object' || err === null || !('status' in err)) {
      return false;
    }
    return err.status === 401 || err.status === 403;
  }

  private getErrorCode(err: unknown): string | number | undefined {
    if (typeof err !== 'object' || err === null || !('code' in err)) {
      return undefined;
    }
    return typeof err.code === 'string' || typeof err.code === 'number' ? err.code : undefined;
  }
}
